// src/pages/Users/Galleries/AlbumGaleriePage.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import useScrollToTop from "../../../hooks/useScollToTop";
import { getGalleryById } from "../../../services/gallerieServices";
import Button from "../../../components/Button/Button";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import {
  FaPlus, FaMinus, FaChevronLeft, FaChevronRight, FaTimes,
  FaExternalLinkAlt, FaDownload, FaShareAlt,
} from "react-icons/fa";
import AddPhotoToAlbumModal from "./AddPhotoToAlbumModal";
import RemovePhotoFromAlbumModal from "./RemovePhotoFromAlbumModal";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// ✅ helper pour rendre les URLs /uploads/... absolues vers l’API
import { toAbsolutePhotoUrl as toAbsPhoto } from "../../../services/albumPhotoServices";

const FALLBACK = "/img/no-image.jpg";
const PAGE_SIZE = 24;

function onImgError(e) { if (e?.currentTarget) e.currentTarget.src = FALLBACK; }
function stripHtml(html = "") {
  const div = document.createElement("div");
  div.innerHTML = html;
  return (div.textContent || div.innerText || "").trim();
}

// -------- Lightbox (Portal) ----------
function Lightbox({ photos, startIndex = 0, onClose }) {
  const [index, setIndex] = useState(startIndex);
  const [purify, setPurify] = useState(null);
  const current = photos[index] || {};

  useEffect(() => {
    (async () => {
      try {
        const mod = await import("dompurify");
        setPurify(() => (mod.default ? mod.default : mod));
      } catch {}
    })();
  }, []);

  const safeDesc = useMemo(() => {
    const raw = current.description || "";
    try { return purify ? purify.sanitize(raw) : raw; } catch { return raw; }
  }, [current.description, purify]);

  const prev = useCallback(() => setIndex((i) => (i - 1 + photos.length) % photos.length), [photos.length]);
  const next = useCallback(() => setIndex((i) => (i + 1) % photos.length), [photos.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, onClose]);

  useEffect(() => {
    const pre = (i) => {
      const p = photos[i];
      if (!p?.photo_url) return;
      const img = new Image();
      img.src = toAbsPhoto(p.photo_url);
    };
    pre((index + 1) % photos.length);
    pre((index - 1 + photos.length) % photos.length);
  }, [index, photos]);

  const onBackdrop = (e) => { if (e.target === e.currentTarget) onClose?.(); };

  const currentSrc = toAbsPhoto(current.photo_url || "");
  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center" onClick={onBackdrop}>
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 p-3 flex items-center justify-between text-white">
        <span className="text-sm opacity-90">{index + 1} / {photos.length}</span>
        <button aria-label="Fermer" title="Fermer" onClick={onClose}
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/70">
          <FaTimes />
        </button>
      </div>

      {/* Image */}
      <div className="max-w-[95vw] max-height-[90vh] w-[min(1100px,95vw)]">
        <img
          src={currentSrc || FALLBACK}
          alt={stripHtml(current.description || `Photo ${index + 1}`)}
          className="w-full max-h-[78vh] object-contain rounded-xl shadow-2xl"
          onError={onImgError}
        />
        {(current.description || current.photo_url) && (
          <div className="mt-3 text-white">
            {current.description ? (
              <div
                className="prose prose-invert prose-sm max-w-none [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1"
                dangerouslySetInnerHTML={{ __html: safeDesc }}
              />
            ) : (
              <div className="text-xs opacity-75 break-all">{currentSrc}</div>
            )}
          </div>
        )}
      </div>

      {/* Nav arrows */}
      {photos.length > 1 && (
        <>
          <button aria-label="Précédente" onClick={prev}
            className="absolute left-4 md:left-6 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 text-white hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/70">
            <FaChevronLeft />
          </button>
          <button aria-label="Suivante" onClick={next}
            className="absolute right-4 md:right-6 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 text-white hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/70">
            <FaChevronRight />
          </button>
        </>
      )}

      {/* Actions image */}
      {currentSrc && (
        <div className="absolute bottom-4 right-4 flex gap-2">
          <a href={currentSrc} target="_blank" rel="noopener noreferrer" title="Ouvrir l’original"
            className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/70">
            <FaExternalLinkAlt />
          </a>
          <a href={currentSrc} download title="Télécharger"
            className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/70">
            <FaDownload />
          </a>
        </div>
      )}
    </div>,
    document.body
  );
}

// -------- Page ----------
export default function AlbumGaleriePage() {
  useScrollToTop();
  const navigate = useNavigate();
  const { id } = useParams();

  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);
  const isManager = userRole === "admin" || userRole === "enseignant";

  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);

  const [isAddPhotoModalOpen, setIsAddPhotoModalOpen] = useState(false);
  const [isRemovePhotoModalOpen, setIsRemovePhotoModalOpen] = useState(false);
  const [newPhotoId, setNewPhotoId] = useState(null);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const [purify, setPurify] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const mod = await import("dompurify");
        setPurify(() => (mod.default ? mod.default : mod));
      } catch {}
    })();
  }, []);

  const fetchGallery = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getGalleryById(Number(id));
      if (response && response.id) setGallery(response);
      else setGallery(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchGallery(); }, [fetchGallery]);

  const photos = useMemo(() => (Array.isArray(gallery?.photos) ? gallery.photos : []), [gallery]);

  const [perPage, setPerPage] = useState(PAGE_SIZE);
  const visible = useMemo(() => photos.slice(0, perPage), [photos, perPage]);

  const openLightbox = (idx) => { setLightboxIndex(idx); setLightboxOpen(true); };

  const copyShare = async () => {
    try {
      const url = `${window.location.origin}${window.location.pathname}`;
      await navigator.clipboard.writeText(url);
      toast.success("Lien de l’album copié !");
    } catch { toast.error("Impossible de copier le lien."); }
  };

  const handleAddPhotoClick = () => {
    setNewPhotoId(null);
    setIsAddPhotoModalOpen(true);
  };

  const albumDescSafe = useMemo(() => {
    const raw = gallery?.description || "";
    try { return purify ? purify.sanitize(raw) : raw; } catch { return raw; }
  }, [gallery?.description, purify]);

  if (!loading && !gallery) {
    return <div className="container mx-auto mt-24 p-4 text-black dark:text-white">Album introuvable</div>;
  }

  return (
    <div className="flex flex-col w-full mx-auto">
      <div className="pt-[150px]" aria-hidden />
      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur">
          <div className="px-4 py-5 sm:px-6 bg-gradient-to-r from-sky-500 via-violet-500 to-orange-400 text-white">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="min-w-0">
                <h2 className="text-lg sm:text-xl font-semibold truncate">{gallery?.titre || "Album"}</h2>
                <p className="text-white/90 text-sm">
                  {photos.length} photo{photos.length > 1 ? "s" : ""}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button text="Retour Galeries" onClick={() => navigate(-1)} className="rounded-xl" />
                <button type="button" onClick={copyShare}
                  className="inline-flex items-center gap-2 rounded-xl ring-1 ring-white/70 text-white/95 hover:bg-white/15 px-3 py-2" title="Copier le lien de l’album">
                  <FaShareAlt /> Partager
                </button>
                {isManager && (
                  <>
                    <button type="button" onClick={handleAddPhotoClick}
                      className="inline-flex items-center gap-2 rounded-xl ring-1 ring-white/70 text-white/95 hover:bg-white/15 px-3 py-2" title="Ajouter une photo">
                      <FaPlus /> Ajouter
                    </button>
                    <button type="button" onClick={() => setIsRemovePhotoModalOpen(true)}
                      className="inline-flex items-center gap-2 rounded-xl ring-1 ring-white/70 text-white/95 hover:bg-white/15 px-3 py-2" title="Retirer une photo">
                      <FaMinus /> Retirer
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {gallery?.description && (
            <div className="px-4 sm:px-6 py-4">
              <div
                className="prose prose-sm dark:prose-invert max-w-none [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1 [&_a]:text-sky-700 dark:[&_a]:text-sky-400"
                dangerouslySetInnerHTML={{ __html: albumDescSafe }}
              />
            </div>
          )}

          <div className="px-4 sm:px-6 pb-6">
            {loading && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Array.from({ length: 9 }).map((_, i) => (
                  <div key={i} className="rounded-xl bg-slate-200/70 dark:bg-slate-800/60 h-48 animate-pulse" />
                ))}
              </div>
            )}

            {!loading && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {visible.map((photo, idx) => {
                    const src = toAbsPhoto(photo.photo_url || "");
                    return (
                      <figure key={photo.id || idx}
                        className="group rounded-xl overflow-hidden ring-1 ring-slate-200 dark:ring-slate-800 bg-slate-50 dark:bg-slate-900">
                        <button type="button" className="block w-full" onClick={() => openLightbox(idx)}
                          title="Voir" aria-label="Voir la photo">
                          <div className="w-full" style={{ aspectRatio: "16 / 9" }}>
                            <img
                              src={src || FALLBACK}
                              alt={stripHtml(photo.description || `Photo ${idx + 1}`)}
                              className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
                              loading="lazy"
                              onError={onImgError}
                            />
                          </div>
                        </button>
                        {photo.description && (
                          <figcaption className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300 line-clamp-2">
                            {stripHtml(photo.description)}
                          </figcaption>
                        )}
                      </figure>
                    );
                  })}

                  {isManager && photos.length > 0 && (
                    <>
                      <button
                        type="button"
                        onClick={handleAddPhotoClick}
                        className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 bg-slate-200 dark:bg-slate-900/60 hover:ring-sky-300 transition grid place-items-center h-48"
                        title="Ajouter une photo"
                      >
                        <div className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
                          <FaPlus /> <span className="text-sm font-semibold">Ajouter</span>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsRemovePhotoModalOpen(true)}
                        className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 bg-slate-200 dark:bg-slate-900/60 hover:ring-sky-300 transition grid place-items-center h-48"
                        title="Retirer des photos"
                      >
                        <div className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
                          <FaMinus /> <span className="text-sm font-semibold">Retirer</span>
                        </div>
                      </button>
                    </>
                  )}
                </div>

                {visible.length < photos.length && (
                  <div className="text-center mt-6">
                    <Button text="Charger plus" onClick={() => setPerPage((p) => p + PAGE_SIZE)} className="rounded-xl" />
                  </div>
                )}

                {photos.length === 0 && (
                  <div className="text-center py-16 text-slate-600 dark:text-slate-300">
                    Aucune photo pour l’instant.
                    {isManager && (
                      <div className="mt-3">
                        <Button text="Ajouter une photo" onClick={handleAddPhotoClick} className="rounded-xl" />
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {lightboxOpen && (
        <Lightbox photos={photos.map(p => ({ ...p, photo_url: toAbsPhoto(p.photo_url) }))} startIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)} />
      )}

      {isAddPhotoModalOpen && (
        <AddPhotoToAlbumModal
          isOpen={isAddPhotoModalOpen}
          onClose={() => { setIsAddPhotoModalOpen(false); fetchGallery(); }}
          galleryId={id}
          photoId={newPhotoId}
        />
      )}
      {isRemovePhotoModalOpen && (
        <RemovePhotoFromAlbumModal
          isOpen={isRemovePhotoModalOpen}
          onClose={() => { setIsRemovePhotoModalOpen(false); fetchGallery(); }}
          galleryId={id}
        />
      )}
      <ToastContainer />
    </div>
  );
}
